<template>
  <div>
    <label>{{ label }}</label>
    <div 
      ref="editor" 
      contenteditable="true" 
      class="editor"
      placeholder="Escreva aqui"
      @paste="handlePaste"
      @drop="handleDrop"
      @dragover.prevent
    >
    </div>
    <p v-if="isLimitReached" class="text-warning">{{ limitMessage }}</p>
  </div>
</template>

<script>
import { ref, getCurrentInstance, onMounted } from 'vue';

export default {
  props: {
    label: {
      type: String,
    },
    limitMessage: {
      type: String,
    },
  },
  setup() {
    const imageCount = ref(0);
    const maxImages = ref(5);
    const maxSize = ref(20 * 1024 * 1024);
    const totalSize = ref(0);
    const editor = ref(null);
    const instance = getCurrentInstance();
    const isLimitReached = ref(false);

    const handlePaste = (event) => {
      event.preventDefault();
      event.stopPropagation();


      if (imageCount.value >= maxImages.value) {
        isLimitReached.value = true;
        return;
      }

      const clipboardItems = event.clipboardData.items;

      for (let i = 0; i < clipboardItems.length; i++) {
        const item = clipboardItems[i];

        if (item.type.indexOf("image") === 0) {
          const file = item.getAsFile();
          processImage(file);
          break;
        }
      }
    };

    const handleDrop = (event) => {
      event.preventDefault();
      event.stopPropagation();


      if (imageCount.value >= maxImages.value) {
        isLimitReached.value = true;
        return;
      }

      const files = event.dataTransfer.files;

      if (files.length > 1) {
        alert("Selecione apenas uma imagem de cada vez.");
        return;
      }

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.type.indexOf("image") === 0) {
          processImage(file);
        }
      }
    };

    const processImage = (file) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const imageBase64 = e.target.result;
        const imageSize = base64ToBytes(imageBase64);
        
        if (totalSize.value + imageSize > maxSize.value) {
          alert("Tamanho máximo de 20MB excedido.");
          return;
        }

        imageCount.value++;
        totalSize.value += imageSize;

        insertImage(imageBase64);
      };

      reader.readAsDataURL(file);
    };

    const base64ToBytes = (base64) => {
      const stringLength = base64.length - "data:image/png;base64,".length;
      return 4 * Math.ceil(stringLength / 3) * 0.5624896334383812;
    };

    const insertImage = (imageBase64) => {
      let imagesArea = editor.value.querySelector(".images-area");
      if (!imagesArea) {
        imagesArea = document.createElement("div");
        imagesArea.classList.add("images-area");
        imagesArea.setAttribute("contenteditable", "false");
        editor.value.appendChild(imagesArea);
      }
      const existingImage = Array.from(imagesArea.querySelectorAll("img")).find(
        (img) => img.src === imageBase64
      );
      if (existingImage) {
        alert("Essa imagem já foi adicionada.");
        return;
      }

      const img = document.createElement("img");
      img.src = imageBase64;
      img.alt = "Imagem inserida";

      const removeButton = document.createElement("button");
      removeButton.innerText = "x";
      removeButton.classList.add("remove-button");
      removeButton.addEventListener("click", () => {
        imagesArea.removeChild(img);
        imagesArea.removeChild(removeButton);
        imageCount.value--;
        totalSize.value -= base64ToBytes(imageBase64);
        isLimitReached.value = false;
      });

      imagesArea.appendChild(img);
      imagesArea.appendChild(removeButton);
    };

    const clearEditor = () => {
      if (editor.value) {
        editor.value.innerHTML = "";
        imageCount.value = 0;
        totalSize.value = 0;
      }
    };

    const getDescription = () => {
      const removeButtons = editor.value.querySelectorAll('.remove-button');
      removeButtons.forEach(button => button.remove());
      return editor.value.innerText.trim();
    };

    const getImages = () => {
      const images = editor.value.querySelectorAll("img");

      return Array.from(images).map((img) => {
        const src = img.src;
        const byteString = atob(src.split(",")[1]);
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const uintArray = new Uint8Array(arrayBuffer);

        for (let i = 0; i < byteString.length; i++) {
          uintArray[i] = byteString.charCodeAt(i);
        }

        const blob = new Blob([uintArray], { type: "image/png" });
        const file = new File([blob], `image-${Date.now()}.png`, { type: "image/png" });
        return file;
      });
    };

    onMounted(() => {
      if (editor.value) {
        editor.value.addEventListener('input', () => {
          const imagesInEditor = editor.value.querySelectorAll("editor img").length;
          isLimitReached.value = imagesInEditor >= maxImages.value;
        });
      }
    });


    return {
      imageCount,
      maxImages,
      handlePaste,
      handleDrop,
      clearEditor,
      getDescription,
      getImages,
      editor,
      isLimitReached
    };
  },
};
</script>

<style>
.editor {
  width: auto;
  height: 150px;
  border: 1px solid #dee2e6;
  border-radius: 5px;
  padding: 10px;
  font-size: 0.875rem;
  font-family: Arial, sans-serif;
  overflow-y: auto;
  word-break: break-all;
  background-color: #fff;
  color: #a3a9ae;
}

.editor:focus {
  border-color: #2dce89;
  outline: none;
}

.editor .images-area {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
  align-items: flex-start;
}

.editor img {
  width: 80px;
  height: auto;
  border: 1px solid #ccc;
  border-radius: 5px;
  position: relative;
}

.remove-button {
  position: relative;
  bottom: 10px;
  right: 15px;
  background: none;
  border: none;
  border-radius: 100%;
  padding: 5px;
  cursor: pointer;
  font-size: 12px;
  color: #525f7f;
  font-weight: bold;
}

label {
  color: #525f7f;
  font-size: 0.875rem;  
  font-weight: 600;
}
</style>
